<template>
  <div :class="['flex', vertical ? 'flex-col' : 'flex-row flex-wrap', `gap-${gap}`]">
    <a
      :href="appStoreLinks.client.ios"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="$ts('landing.footer.appStore')"
      :style="{ width: `${width}px` }"
      class="block"
    >
      <img
        :src="appleBadgeUrl"
        :alt="$ts('landing.footer.appStore')"
        class="h-auto w-full"
      />
    </a>
    <a
      :href="appStoreLinks.client.android"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="$ts('landing.footer.googlePlay')"
      :style="{ width: `${width}px` }"
      class="block"
    >
      <img
        :src="googlePlayBadgeUrl"
        :alt="$ts('landing.footer.googlePlay')"
        class="h-auto w-full"
      />
    </a>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '#imports';
import { appStoreLinks } from '#bubba/shared/config/links';

const props = withDefaults(defineProps<{
  /** Badge width in pixels — both badges are constrained to the same width */
  width?: number
  /** Gap between badges (Tailwind spacing scale) */
  gap?: number | string
  /** Stack vertically instead of side-by-side */
  vertical?: boolean
}>(), {
  width: 160,
  gap: 3,
  vertical: true,
});

const { $ts, $getLocale } = useI18n();

const CDN = 'https://cdn.bubba.pet/assets/badges';

/**
 * Google Play badge filenames per website locale.
 * Pattern: getitongoogleplay_badge_web_color_{language}.svg
 * Note: Google spells Ukrainian as "ukranian" (typo) — keep as-is.
 */
const googlePlayBadges: Record<string, string> = {
  en: `${CDN}/google-play/getitongoogleplay_badge_web_color_english.svg`,
  lt: `${CDN}/google-play/getitongoogleplay_badge_web_color_lithuanian.svg`,
  lv: `${CDN}/google-play/getitongoogleplay_badge_web_color_latvian.svg`,
  ee: `${CDN}/google-play/getitongoogleplay_badge_web_color_estonian.svg`,
  pl: `${CDN}/google-play/getitongoogleplay_badge_web_color_polish.svg`,
  ru: `${CDN}/google-play/getitongoogleplay_badge_web_color_russian.svg`,
  ua: `${CDN}/google-play/getitongoogleplay_badge_web_color_ukranian.svg`,
};

/**
 * Apple App Store badge URLs per website locale.
 * URLs are explicit because Apple uses inconsistent naming:
 * - English: us-uk locale code, date 092917
 * - Ukrainian: ua locale code, no _rgb_ segment, date 082124
 * - Most others: 2-letter country code with _rgb_blk_100317
 * - Estonian: not available on CDN — falls back to English
 */
const appleBadges: Record<string, string> = {
  en: `${CDN}/apple/download_on_the_app_store_badge_us-uk_rgb_blk_092917.svg`,
  lt: `${CDN}/apple/download_on_the_app_store_badge_lt_rgb_blk_100317.svg`,
  lv: `${CDN}/apple/download_on_the_app_store_badge_lv_rgb_blk_100317.svg`,
  ee: `${CDN}/apple/download_on_the_app_store_badge_us-uk_rgb_blk_092917.svg`, // Estonian not available, fallback to English
  pl: `${CDN}/apple/download_on_the_app_store_badge_pl_rgb_blk_100317.svg`,
  ru: `${CDN}/apple/download_on_the_app_store_badge_ru_rgb_blk_100317.svg`,
  ua: `${CDN}/apple/download_on_the_app_store_badge_ua_blk_082124.svg`,
};

const locale = computed(() => $getLocale());

const googlePlayBadgeUrl = computed(() => googlePlayBadges[locale.value] ?? googlePlayBadges.en);
const appleBadgeUrl = computed(() => appleBadges[locale.value] ?? appleBadges.en);
</script>
